import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Breadcrumb,
  Button,
  Flex,
  Heading,
  Separator,
  Spinner,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Text,
} from '@optiaxiom/react';
import { IconArrowRotateLeft, IconXmark } from '@optimizely/axiom-icons';
import { useDecision } from '@optimizely/react-sdk';

import { ConfirmationModal } from '@frontend/components/confirmationModal/components/ConfirmationModal';
import { Sidebar } from '@frontend/components/sidebar/Sidebar';
import { ToolRowDetailsSidebar } from '@frontend/components/tools-table/ToolRowDetailsSidebar';
import { FeatureFlag } from '@frontend/contexts';
import {
  useAgentId,
  useAgentVersions,
  useCreateSpecializedAgent,
  useDeleteSpecializedAgent,
  useGetSpecializedAgent,
  useSpecializedAgentReducer,
  useUnusedParameters,
  useUpdateSpecializedAgent,
} from '@frontend/features/agents/hooks';
import {
  useInstanceId,
  useNamespacedNavigate,
  useObjectTitle,
  useStickyHeader,
} from '@frontend/hooks';
import { AppPagePadding } from '@frontend/styles';
import * as agentStyles from '@frontend/styles/form.css';
import { SIDEBAR_WIDTH } from '@frontend/styles/layout.css';
import { SpecializedAgentListItem } from '@frontend/types';
import {
  AgentParameter,
  AgentParameterType,
  SpecializedAgent,
  SpecializedAgentExecution,
} from '@frontend/types/specialized-agent';
import { Tool } from '@frontend/types/tools';
import { formatDate } from '@frontend/utils/dateUtils';

import { agentExecutionsService } from '../../../services/agentExecutions';
import { AgentActionButtons } from '../../shared/AgentActionButtons';
import { AgentBasicFields } from '../../shared/AgentBasicFields';
import { AgentExecutionForm } from '../agent-execution/AgentExecutionForm';
import { AgentVersionHistorySidebar } from '../agent-version-history/AgentVersionHistorySidebar';
import { AppCompanionTab } from '../app-companion/AppCompanionTab';
import { SpecializedAgentExecutionDetailsSidebar } from '../executions/SpecializedAgentExecutionDetailsSidebar';
import { SpecializedAgentExecutionsTab } from '../executions/SpecializedAgentExecutionsTab';

import { AgentFormInput, AgentFormOutput, VariableRow } from './components';
import * as styles from './SpecializedAgentForm.css';

export function SpecializedAgentCreationForm({
  agentListItem,
  isEmbedded = false,
  onClose,
  backHref,
  isStickyHeader = false,
}: {
  agentListItem?: SpecializedAgentListItem;
  isEmbedded?: boolean;
  onClose?: () => void;
  backHref?: string | null;
  isStickyHeader?: boolean;
}) {
  const navigate = useNamespacedNavigate();
  const location = useLocation();
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { instanceId } = useInstanceId();
  const [app_companion_ff] = useDecision('app_companion');

  const hasAppCompanionFF = useMemo(() => app_companion_ff?.enabled ?? false, [app_companion_ff]);

  // Get agent ID from URL params
  const agentIdFromUrl = params.agentId;
  const executionIdFromUrl = searchParams.get('executionId');
  const isCopyMode = location.pathname.includes('/copy');
  const isEditing = !!agentIdFromUrl && !isCopyMode; // If there's an agentId in URL and not copy mode, we're editing

  const activeTab = useMemo((): string => {
    const tabFromQuery = searchParams.get('tab');
    switch (tabFromQuery) {
      case 'app-companion':
        return 'app-companion';
      case 'execution':
        return 'execution';
      default:
        return 'form';
    }
  }, [searchParams]);

  const goBackToBackHref = useCallback(() => {
    navigate(backHref ? backHref : '/agents');
  }, [backHref, navigate]);

  // Accept agent from either prop or navigation state, but prioritize URL-based loading
  const navigationState = location.state as
    | { specializedAgentItem?: SpecializedAgentListItem }
    | undefined;
  const currentAgentListItem = agentListItem || navigationState?.specializedAgentItem || undefined;

  // Build agent details URL from URL param if available, otherwise use the one from navigation state
  const agentDetailsUrl = agentIdFromUrl
    ? `/hypatia/api/v1/${instanceId}/agents/specialized/${agentIdFromUrl}`
    : currentAgentListItem?.links.self;

  const { agent: detailedAgent, isPending: isAgentLoading } = useGetSpecializedAgent(
    agentDetailsUrl || ''
  );

  const [formData, dispatch] = useSpecializedAgentReducer(detailedAgent || undefined);

  const agentVersions = useAgentVersions(formData.id ?? '');
  const isViewingPreviousVersion = !!agentVersions.selectedVersionSummary;

  // Set document title for the agent
  useObjectTitle(formData.name);

  // State for sidebar and execution selection - declare early to avoid hoisting issues
  const [selectedExecutionId, setSelectedExecutionId] = useState<string | null>(executionIdFromUrl);
  const [selectedExecution, setSelectedExecution] = useState<SpecializedAgentExecution | null>(
    null
  );
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [isVersionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [selectedTool, setSelectedTool] = useState<Tool | null>(null);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isAgentFormInputOpen, setIsAgentFormInputOpen] = useState(true);
  const [isUnusedDialogOpen, setIsUnusedDialogOpen] = useState(false);
  const [shouldShowUnusedParamsWarning, setShouldShowUnusedParamsWarning] = useState(false);

  // Sticky header detection
  const { sentinelRef, isSticky } = useStickyHeader(isStickyHeader);

  // Use version data when viewing a previous version, otherwise use current form data
  const displayData =
    isViewingPreviousVersion && agentVersions.selectedVersionData
      ? agentVersions.selectedVersionData
      : formData;

  const { unusedParams } = useUnusedParameters(
    formData.parameters || [],
    formData.prompt_template || ''
  );

  const navigateToAgentsPage = () => {
    if (isEmbedded && onClose) {
      onClose();
    } else {
      goBackToBackHref();
    }
  };

  useEffect(() => {
    if (detailedAgent && isCopyMode) {
      // In copy mode, transform the data for new agent creation
      const copiedData: Partial<SpecializedAgent> = {
        ...detailedAgent,
        id: '', // Clear ID to ensure new agent creation
        name: `${detailedAgent.name} copy`, // Suffix name
        created_at: '',
        updated_at: '',
        is_system_agent: false, // Copied agents are not system agents
        locked_fields: [], // Clear locked fields
        // Clear any other fields that should be reset for copy
      };
      dispatch({ type: 'SET_ALL', payload: copiedData as SpecializedAgent });
    } else if (detailedAgent && isEditing) {
      // In edit mode, use the original data
      dispatch({ type: 'SET_ALL', payload: detailedAgent });
    }
    // Only run when detailedAgent changes or mode changes
  }, [detailedAgent, dispatch, isCopyMode, isEditing]);

  // Function to search for execution directly when opening from URL
  const searchForExecutionDirectly = useCallback(
    async (executionId: string) => {
      if (!instanceId || !formData.id) return;

      try {
        // Try pages 1-5 to find the execution
        for (let page = 1; page <= 5; page++) {
          const pageData = await agentExecutionsService.getAgentExecutions(
            instanceId,
            formData.id,
            page,
            20
          );

          if (pageData?.items) {
            const execution = pageData.items.find(exec => exec.id === executionId);
            if (execution) {
              setSelectedExecution(execution);
              return;
            }
          }
        }

        console.warn('Execution not found in first 5 pages:', executionId);
      } catch (error) {
        console.error('Error searching for execution:', error);
      }
    },
    [instanceId, formData.id]
  );

  // Handle URL changes and update sidebar state accordingly
  useEffect(() => {
    const executionIdFromUrl = searchParams.get('executionId');
    if (executionIdFromUrl) {
      setSidebarOpen(false);
      setVersionHistoryOpen(false);
      setSelectedTool(null);

      setSelectedExecutionId(executionIdFromUrl);
      // If we have an execution ID but no execution object, search for it
      if (executionIdFromUrl !== selectedExecution?.id && formData.id) {
        searchForExecutionDirectly(executionIdFromUrl);
      }
    } else {
      setSelectedExecutionId(null);
    }
  }, [searchParams, selectedExecution?.id, formData.id, searchForExecutionDirectly]);

  const clearExecutionSelection = () => {
    searchParams.delete('executionId');
    setSearchParams(searchParams);
    setSelectedExecutionId(null);
    setSelectedExecution(null);
  };

  const openSidebar = useCallback(
    (
      sidebar: 'testRun' | 'versionHistory' | 'toolDetails' | 'executionDetails',
      data?: { executionId?: string; execution?: SpecializedAgentExecution; tool?: Tool }
    ) => {
      setSidebarOpen(false);
      setVersionHistoryOpen(false);
      setSelectedTool(null);
      agentVersions.resetSelection();

      if (sidebar !== 'executionDetails') {
        searchParams.delete('executionId');
        setSearchParams(searchParams);
        setSelectedExecutionId(null);
        setSelectedExecution(null);
      }

      switch (sidebar) {
        case 'testRun':
          setSidebarOpen(true);
          break;
        case 'versionHistory':
          setVersionHistoryOpen(true);
          break;
        case 'toolDetails':
          setSelectedTool(data?.tool ?? null);
          break;
        case 'executionDetails':
          if (data?.executionId) {
            searchParams.set('executionId', data.executionId);
            setSearchParams(searchParams);
            setSelectedExecutionId(data.executionId);
            if (data.execution) {
              setSelectedExecution(data.execution);
            }
          }
          break;
      }
    },
    [agentVersions, searchParams, setSearchParams]
  );

  const { mutate: createAgent, isPending: isCreating } = useCreateSpecializedAgent(
    instanceId ?? '',
    (createdAgent: SpecializedAgent) => {
      dispatch({ type: 'SET_ALL', payload: createdAgent });
      if (isEmbedded && onClose) {
        onClose();
        return;
      }
      navigate(`/agents/specialized/${createdAgent.id}`, { replace: true });
    }
  );

  const { mutate: updateAgent, isPending: isUpdating } = useUpdateSpecializedAgent(
    agentDetailsUrl || '',
    () => {
      agentVersions.resetSelection();
    }
  );

  const { mutate: deleteAgent, isPending: isDeleting } =
    useDeleteSpecializedAgent(navigateToAgentsPage);

  const {
    isCheckingAvailability,
    availabilityStatus,
    idFieldStatus,
    isSaveDisabled: isIdCheckDisabled,
  } = useAgentId(instanceId, formData.agent_id, isEditing, detailedAgent?.agent_id);

  // Check if required fields are filled
  const isRequiredFieldsEmpty =
    !formData.name?.trim() || !formData.agent_id?.trim() || !formData.description?.trim();

  const hasFormChanged = useMemo(() => {
    if (!detailedAgent || !isEditing) return true;
    return JSON.stringify(formData) !== JSON.stringify(detailedAgent);
  }, [formData, detailedAgent, isEditing]);

  const isSaveDisabled =
    isIdCheckDisabled || isCreating || isUpdating || (isEditing && !hasFormChanged);

  const executeAction = useCallback(() => {
    if (isEditing) {
      updateAgent(formData);
    } else {
      createAgent(formData);
    }
  }, [isEditing, updateAgent, createAgent, formData]);

  const handleSaveClick = useCallback(() => {
    if (isRequiredFieldsEmpty) {
      setShowValidationErrors(true);
      return;
    }

    if (unusedParams.length > 0) {
      setIsUnusedDialogOpen(true);
      return;
    }

    executeAction();
  }, [isRequiredFieldsEmpty, unusedParams, executeAction]);

  // Show loading spinner if agent details are loading
  if (isAgentLoading && currentAgentListItem) {
    return (
      <Flex alignItems="center" justifyContent="center" style={{ minHeight: '300px' }}>
        <Spinner />
      </Flex>
    );
  }

  const isAnySidebarOpen =
    isSidebarOpen ||
    isVersionHistoryOpen ||
    !!selectedExecutionId ||
    !!selectedExecution ||
    !!selectedTool;

  return (
    <Flex gap="0" className={clsx(AppPagePadding, isAnySidebarOpen && styles.sidebarOpenMargin)}>
      {/* Sentinel element for sticky detection */}
      {isStickyHeader && <Box ref={sentinelRef} w="full" className={styles.stickySentinel} />}

      <Tabs
        value={activeTab}
        onValueChange={value => {
          if (value === 'form') {
            searchParams.delete('tab');
            searchParams.delete('page');
          } else {
            searchParams.set('tab', value);
          }
          setSearchParams(searchParams, { replace: true });
        }}
        className={
          isAnySidebarOpen
            ? agentStyles.FormAgentMaxWidthSidebarOpen
            : agentStyles.FormAgentMaxWidth
        }
      >
        <Flex
          gap="0"
          flexDirection="column"
          className={clsx(
            isStickyHeader && styles.stickyBackButton,
            styles.headerContainer,
            isStickyHeader && isSticky && styles.headerContainerSticky
          )}
        >
          {/* Breadcrumb row - always visible, contains inline title/buttons when sticky */}
          <Flex flexDirection="row" alignItems="center" justifyContent="space-between" h="md">
            <Flex flexDirection="row" alignItems="center" gap="0">
              <Breadcrumb
                items={[
                  {
                    execute: () => {
                      if (isEmbedded && onClose) {
                        onClose();
                      } else {
                        goBackToBackHref();
                      }
                    },
                    label: 'Agents',
                  },
                  { label: ' ' },
                ]}
              />
              {/* Inline title - appears when sticky */}
              <Heading
                level="3"
                color="fg.default"
                className={clsx(
                  styles.inlineTitle,
                  isStickyHeader && isSticky && styles.inlineTitleSticky
                )}
                truncate
              >
                {formData.name || 'Specialized Agent'}
              </Heading>
            </Flex>

            {/* Inline buttons - appear when sticky */}
            <Flex
              flexDirection="row"
              className={clsx(
                styles.inlineButtons,
                isStickyHeader && isSticky && styles.inlineButtonsSticky
              )}
            >
              {isEmbedded && onClose && (
                <Button
                  appearance="subtle"
                  size="sm"
                  square
                  icon={<IconXmark />}
                  onClick={onClose}
                  aria-label="Close SA creation"
                />
              )}
              {!isViewingPreviousVersion && (
                <AgentActionButtons
                  type="specialized"
                  agentId={formData.id}
                  isTestRunDisabled={!formData.id}
                  isEditing={isEditing}
                  isSaveDisabled={isSaveDisabled}
                  isCreating={isCreating}
                  isUpdating={isUpdating}
                  onSave={handleSaveClick}
                  onTestRun={() => openSidebar('testRun')}
                  onViewHistory={formData.id ? () => openSidebar('versionHistory') : undefined}
                  onDelete={() => setDeleteDialogOpen(true)}
                  isEmbedded={isEmbedded}
                />
              )}
            </Flex>
          </Flex>

          {/* Title row - visible initially, collapses when sticky */}
          <Flex
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
            h="md"
            className={clsx(styles.titleRow, isStickyHeader && isSticky && styles.titleRowSticky)}
          >
            <Heading
              level="3"
              color="fg.default"
              className={clsx(
                styles.titleRowTitle,
                isStickyHeader && isSticky && styles.titleRowTitleSticky
              )}
              truncate
            >
              {formData.name || 'Specialized Agent'}
            </Heading>
            <Flex
              flexDirection="row"
              className={clsx(
                styles.titleRowButtons,
                isStickyHeader && isSticky && styles.titleRowButtonsSticky
              )}
            >
              {isEmbedded && onClose && (
                <Button
                  appearance="subtle"
                  size="sm"
                  square
                  icon={<IconXmark />}
                  onClick={onClose}
                  aria-label="Close SA creation"
                />
              )}
              {!isViewingPreviousVersion && (
                <AgentActionButtons
                  type="specialized"
                  agentId={formData.id}
                  isTestRunDisabled={!formData.id}
                  isEditing={isEditing}
                  isSaveDisabled={isSaveDisabled}
                  isCreating={isCreating}
                  isUpdating={isUpdating}
                  onSave={handleSaveClick}
                  onTestRun={() => openSidebar('testRun')}
                  onViewHistory={formData.id ? () => openSidebar('versionHistory') : undefined}
                  onDelete={() => setDeleteDialogOpen(true)}
                  isEmbedded={isEmbedded}
                />
              )}
            </Flex>
          </Flex>
          {isViewingPreviousVersion &&
            agentVersions.selectedVersionSummary &&
            (() => {
              const versionSummary = agentVersions.selectedVersionSummary;
              return (
                <Alert mt={isSticky ? '0' : '12'} intent="information">
                  <Flex
                    flexDirection="row"
                    justifyContent="space-between"
                    alignItems="center"
                    w="full"
                  >
                    <Text>
                      You are viewing {agentVersions.selectedVersionDisplayLabel} created on{' '}
                      {formatDate(versionSummary.changed_at)}.
                    </Text>
                    {!detailedAgent?.is_system_agent && (
                      <Button
                        appearance="default"
                        icon={<IconArrowRotateLeft size="16" />}
                        iconPosition="start"
                        size="sm"
                        onClick={() =>
                          agentVersions.restoreDialog.onOpen(
                            versionSummary,
                            agentVersions.selectedVersionDisplayLabel
                          )
                        }
                      >
                        Restore
                      </Button>
                    )}
                  </Flex>
                </Alert>
              );
            })()}
          {formData.is_system_agent && (
            <Alert mt="12" intent="information">
              This is an Optimizely-managed agent. Some fields are locked to ensure consistent
              performance.
            </Alert>
          )}
          {formData.id ? (
            <Flex mt={isSticky && !isViewingPreviousVersion ? '0' : '12'}>
              <TabsList>
                <TabsTrigger value="form">Setup</TabsTrigger>
                {hasAppCompanionFF && (
                  <TabsTrigger value="app-companion" disabled={isViewingPreviousVersion}>
                    In-app Triggers
                  </TabsTrigger>
                )}
                <TabsTrigger value="execution" disabled={isViewingPreviousVersion}>
                  Logs
                </TabsTrigger>
              </TabsList>
            </Flex>
          ) : (
            <Box mt={isSticky ? '0' : '12'}>
              <Separator />
            </Box>
          )}
        </Flex>

        <TabsContent py="24" value="form">
          <Flex flexDirection="column" gap="24">
            <AgentBasicFields
              lockedFields={isCopyMode ? [] : detailedAgent?.locked_fields || []}
              isInstalled={isCopyMode ? false : !!detailedAgent?.is_installed}
              name={displayData.name || ''}
              isActive={displayData.is_active}
              agentId={displayData.agent_id}
              agentType="specialized"
              idFieldStatus={idFieldStatus}
              isCheckingAvailability={isCheckingAvailability}
              availabilityStatus={availabilityStatus}
              description={displayData.description || ''}
              isEnabledInChat={displayData.is_enabled_in_chat}
              onChange={(field, value) => {
                dispatch({
                  type: 'UPDATE_FIELD',
                  field: field as keyof SpecializedAgent,
                  value: value as SpecializedAgent[keyof SpecializedAgent],
                });
                // Clear validation errors when user starts fixing the fields
                if (showValidationErrors) {
                  setShowValidationErrors(false);
                }
              }}
              onSuggestionClick={suggestion =>
                dispatch({ type: 'UPDATE_FIELD', field: 'agent_id', value: suggestion })
              }
              showErrors={showValidationErrors}
              disabled={isViewingPreviousVersion}
            />
            <AgentFormInput
              lockedFields={isCopyMode ? [] : detailedAgent?.locked_fields || []}
              formData={displayData}
              dispatch={dispatch}
              open={isAgentFormInputOpen}
              onOpenChange={setIsAgentFormInputOpen}
              shouldLoadPromptTemplate={isEditing || isCopyMode}
              shouldShowUnusedParamsWarning={shouldShowUnusedParamsWarning}
              disabled={isViewingPreviousVersion}
              selectedTool={selectedTool}
              onToolSelect={tool =>
                tool && !isEmbedded ? openSidebar('toolDetails', { tool }) : setSelectedTool(null)
              }
            />
            <AgentFormOutput
              formData={displayData}
              dispatch={dispatch}
              lockedFields={isCopyMode ? [] : detailedAgent?.locked_fields || []}
              disabled={isViewingPreviousVersion}
            />

            <Separator />

            <Text fontSize="sm" color="fg.error">
              * Required fields
            </Text>
          </Flex>
        </TabsContent>

        {formData.id && (
          <TabsContent py="24" value="execution">
            <SpecializedAgentExecutionsTab
              agentId={formData.id}
              selectedExecutionId={selectedExecutionId}
              onExecutionSelect={(executionId, execution) =>
                executionId
                  ? openSidebar('executionDetails', {
                      executionId,
                      execution: execution ?? undefined,
                    })
                  : clearExecutionSelection()
              }
              baselineEvaluationScore={formData.baseline_evaluation_score ?? undefined}
            />
          </TabsContent>
        )}

        {formData.id && hasAppCompanionFF && (
          <FeatureFlag featureKey="app_companion">
            <TabsContent value="app-companion">
              <AppCompanionTab agentId={formData.id || ''} />
            </TabsContent>
          </FeatureFlag>
        )}
      </Tabs>

      <Sidebar
        open={isSidebarOpen && !selectedExecutionId}
        dock="right"
        initialWidth={SIDEBAR_WIDTH}
      >
        <AgentExecutionForm agent={formData} hideRightSidebar={() => setSidebarOpen(false)} />
      </Sidebar>

      {/* Execution Details Sidebar - independent of disclosure state */}
      <Sidebar
        open={!!selectedExecution || !!selectedExecutionId}
        dock="right"
        initialWidth={SIDEBAR_WIDTH}
      >
        {(selectedExecution || selectedExecutionId) && (
          <SpecializedAgentExecutionDetailsSidebar
            executionId={selectedExecution?.id || selectedExecutionId || ''}
            onClose={clearExecutionSelection}
          />
        )}
      </Sidebar>

      {/* Version History Sidebar */}
      <Sidebar open={isVersionHistoryOpen} dock="right" initialWidth={SIDEBAR_WIDTH}>
        {formData.id && (
          <AgentVersionHistorySidebar
            agentVersions={agentVersions}
            onClose={() => {
              setVersionHistoryOpen(false);
              agentVersions.resetSelection();
            }}
            isSystemAgent={detailedAgent?.is_system_agent}
          />
        )}
      </Sidebar>

      {/* Tool Details Sidebar */}
      <Sidebar open={selectedTool !== null} dock="right" initialWidth={SIDEBAR_WIDTH}>
        {selectedTool && (
          <ToolRowDetailsSidebar
            selectedTool={selectedTool}
            onClose={() => setSelectedTool(null)}
          />
        )}
      </Sidebar>

      <ConfirmationModal
        isShown={deleteDialogOpen}
        title="Delete Agent"
        action="Delete"
        appearance="danger"
        onCancel={() => setDeleteDialogOpen(false)}
        onConfirm={() => {
          const deleteUrl = detailedAgent?.links?.delete || currentAgentListItem?.links.delete;
          if (deleteUrl) {
            deleteAgent(deleteUrl);
          }
          setDeleteDialogOpen(false);
        }}
        confirmLoading={isDeleting}
        cancelDisabled={isDeleting}
      >
        <Flex flexDirection="column" gap="4">
          <Text>
            You are about to permanently delete the agent{' '}
            <Text fontWeight="600">{formData.name}</Text>
          </Text>
          <Text>Do you want to continue?</Text>
        </Flex>
      </ConfirmationModal>

      <ConfirmationModal
        isShown={isUnusedDialogOpen}
        title="Unused variables found"
        onCancel={() => {
          setIsUnusedDialogOpen(false);
          setIsAgentFormInputOpen(true);
          setShouldShowUnusedParamsWarning(true);
        }}
        onConfirm={() => {
          setIsUnusedDialogOpen(false);
          executeAction();
          setShouldShowUnusedParamsWarning(true);
        }}
        cancelLabel="Go back and review"
        action="Save anyway"
        appearance="primary"
        size="md"
      >
        <Flex flexDirection="column" gap="16">
          <Text>The following variables should be referenced in prompt template.</Text>
          {unusedParams.length > 0 && (
            <Flex flexDirection="column" gap="8">
              {unusedParams.map((param: AgentParameter<AgentParameterType>) => (
                <VariableRow key={param.name} variable={param} onDelete={() => {}} isReadOnly />
              ))}
            </Flex>
          )}
        </Flex>
      </ConfirmationModal>

      <ConfirmationModal
        isShown={agentVersions.restoreDialog.isOpen}
        title="Restore Version"
        onCancel={agentVersions.restoreDialog.onCancel}
        onConfirm={agentVersions.restoreDialog.onConfirm}
        action="Restore Version"
        confirmLoading={agentVersions.restoreDialog.isLoading}
        size="md"
      >
        <Text>
          Restoring this version will replace your current setup. A new version will be created.
        </Text>
      </ConfirmationModal>
    </Flex>
  );
}
